import React from "react";
import { useCurrentTenant } from "@drift/oneplatfront";
import { faCheck, faLinkSlash, faPenToSquare, faPlusCircle, faSpinnerThird } from "@fortawesome/pro-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Trans, useTranslation } from "@onefront/react-sdk";
import {
    BoxForm,
    Description,
    FormHeaderTitle,
    Name,
    SecurityEnvironmentsData,
    SignersLookup,
    useWsGet
} from "@onefront/workspace-forms";
import { VaporTag } from "@vapor/react-custom";
import { ExtendedTypography, ExtendedTab as Tab, ExtendedTabs as Tabs } from "@vapor/react-extended";
import {
    Box,
    Button,
    CircularProgress,
    FormControlLabel,
    FormHelperText,
    Grid,
    Stack,
    Toggle
} from "@vapor/react-material";
import { BaseSyntheticEvent, SyntheticEvent, useEffect, useState } from "react";
import { InfoBox } from "../../../../../../../components/infobox";
import { IntermediaryFooterForm } from "./IntermediaryFooterForm";
import { diffObjects } from "../../../../../../../utils/functions/generic";

const enum TabValue {
    securityenv = "securityenv",
    signaturecert = "signaturecert"
}

export const IntermediarySecurityEnvForm = ({
    data,
    onSubmit,
    onBack,
    loadingPost,
    loadingPatch,
    mode,
    intermediaryTaxId,
    getIntermediary
}) => {
    const { t } = useTranslation();
    const [displayErrors, setDisplayErrors] = useState<any>(false);
    const [errors, setErrors] = useState<any>();
    const [handleCertificate, setHandleCertificate] = useState(false);
    const [editingCompanyCertificate, setEditingCompanyCertificate] = useState(true);
    const [tabValue, setTabValue] = useState<keyof typeof TabValue | any>("securityenv");
    const [mount, setMount] = useState<any>();
    const [body, setBody] = useState<any>(
        mode === "CREATE" && !intermediaryTaxId
            ? [
                  {
                      id: "-1",
                      entratelCode: ""
                  }
              ]
            : []
    );
    const [bodyOriginal, setBodyOriginal] = useState<any>([]);
    const [savedEnv, setSavedEnv] = useState<any>();
    const [editing, setEditing] = useState<string>(mode === "CREATE" ? "-1" : "");
    const tenant = useCurrentTenant();
    const [signer, setSigner] = useState<any>(null);
    const [linkingSigner, setLinkingSigner] = useState<boolean>(false);
    const [editSigner, setEditSigner] = useState<boolean>(false);
    const [loadSignerSelected, setLoadSignerSelected] = useState<boolean>(false);

    const { fetch: getSecurityEnv, loading: loadingSecurityEnv } = useWsGet("");

    const { data: dataSigners, fetch: searchSigners } = useWsGet(
        "rnd:core:workspace-bff:api://v1/intermediaries/signers"
    );

    useEffect(() => {
        if (mode === "EDIT" && loadSignerSelected) {
            setTimeout(() => {
                setLoadSignerSelected(false);
            }, 1500);
        }
    }, [mode, loadSignerSelected]);

    useEffect(() => {
        if (!mount && tenant?.itemId && intermediaryTaxId) {
            setMount(true);
            setEditing("false");
            getSecurityEnv({
                url: `rnd:core:intermediary-registry-read:api://v1/${tenant?.itemId}/intermediary/${intermediaryTaxId}/security-environment`,
                params: {
                    withFiles: true
                }
            }).then(res => {
                if (res.data.length === 0) {
                    setBody([
                        {
                            id: "-1",
                            entratelCode: ""
                        }
                    ]);
                    setEditing("-1");
                } else {
                    setBody(
                        res.data.map((item: any) => {
                            return {
                                ...item,
                                files: item.files.map((file: any) => {
                                    return {
                                        ...file,
                                        size: atob(file?.content)?.length
                                    };
                                }),
                                id: item.siteCode
                            };
                        })
                    );
                    setBodyOriginal(
                        res.data.map((item: any) => {
                            return {
                                ...item,
                                files: item.files.map((file: any) => {
                                    return {
                                        ...file,
                                        size: atob(file?.content)?.length
                                    };
                                }),
                                id: item.siteCode
                            };
                        })
                    );
                }
            });

            searchSigners({
                params: {
                    itemId: tenant?.identifiers?.find(x => x.type === "TAX_ID")?.value,
                    fullText: intermediaryTaxId
                }
            });
        }
    }, [mount, getSecurityEnv, tenant, intermediaryTaxId, searchSigners, data]);

    useEffect(() => {
        if (data?.defaultSigner) {
            setHandleCertificate(true);
            setSigner(data?.defaultSigner?.taxId);

            if (data?.type === "C") {
                setEditingCompanyCertificate(false);
            }
        } else {
            setHandleCertificate(false);
            setSigner(null);
            setEditingCompanyCertificate(true);
        }
    }, [data]);

    const handleSubmit = () => {
        setDisplayErrors(errors);

        if (Object.keys(errors).some(key => errors[key])) {
            return;
        } else {
            onSubmit({
                securityEnvironments: savedEnv,
                defaultSignerTaxId: signer
            });
        }
    };

    const handleSaveSigner = () => {
        setDisplayErrors({ ...errors, signer: !signer && handleCertificate });

        if (Object.keys(errors).some(key => errors[key]) || (!signer && handleCertificate && data?.type === "C")) {
            return;
        } else {
            onSubmit({
                defaultSignerTaxId: signer
            }).then(x => {
                setEditSigner(false);
            });
        }
    };

    const handleAddSecurityEnv = () => {
        setBody((prev: any) => {
            return [
                ...prev,
                {
                    id: "-1",
                    entratelCode: ""
                }
            ];
        });
        setEditing("-1");
    };

    const handleChange = (id: string, data: any) => {
        setBody(prev => {
            return prev.map((item: any) => {
                if (item.id === id) {
                    return {
                        ...item,
                        ...data
                    };
                }
                return item;
            });
        });
    };

    const checkDuplicatesFiles = box => {
        let duplicates = 0;
        bodyOriginal
            ?.find(x => x?.id === box?.id)
            ?.files?.map(file => {
                if (
                    box?.files?.find(x => x?.name === file?.name) &&
                    !diffObjects(
                        file,
                        box?.files?.find(x => x?.name === file?.name)
                    )
                ) {
                    duplicates++;
                }
                return null;
            });
        return duplicates;
    };

    return (
        <Stack spacing={5} direction="column" maxWidth={741}>
            <FormHeaderTitle
                title={"Strumenti di firma"}
                subtitle="Informazioni sugli strumenti di firma"
                sx={{ marginBottom: 0 }}
            />
            <Tabs
                value={tabValue}
                variant="standard"
                size="small"
                onChange={(e: SyntheticEvent, value) => setTabValue(value)}
            >
                <Tab value={"securityenv"} label={t("intermediaries.signatureTools.securityEnv")} />
                <Tab value={"signaturecert"} label={t("intermediaries.signatureTools.signatureCertificate")} />
            </Tabs>

            <Box
                component={"div"}
                gap={3}
                flexDirection={"column"}
                display={tabValue === "securityenv" ? "flex" : "none"}
            >
                {loadingSecurityEnv && (
                    <Stack justifyContent={"center"} alignItems={"center"} height={300}>
                        <CircularProgress />
                    </Stack>
                )}
                {!loadingSecurityEnv &&
                    body?.map((box, index) => (
                        <BoxForm
                            key={`security-env-${box.id}`}
                            body={box}
                            editing={editing === box.id}
                            label={"Ambiente di sicurezza"}
                            tag={index === 0 && <VaporTag label="PREDEFINITO" variant="ghost" type="blueSapphire" />}
                            disableCancel={index === 0 || mode === "EDIT"}
                            disableSubmit={checkDuplicatesFiles(box) === 3}
                            onEdit={(id: string) => {
                                setEditing(id);
                                setErrors({});
                            }}
                            onCancel={id => {
                                setDisplayErrors(false);
                                setBody(prev => {
                                    return prev.filter((item: any) => item.id !== id);
                                });
                                setEditing("false");
                            }}
                            onReset={
                                body?.length > 1 || (body?.length < 2 && mode === "EDIT")
                                    ? (id, data) => {
                                          setMount(false);
                                          setEditing("false");
                                          window.scrollTo(0, 0);
                                      }
                                    : null
                            }
                            onSubmit={() => {
                                setDisplayErrors(errors);

                                if (!box?.expirationDate) {
                                    setDisplayErrors((prev: any) => {
                                        return {
                                            ...prev,
                                            password: true
                                        };
                                    });
                                }

                                if (Object.keys(errors).some(key => errors[key]) || !box?.expirationDate) {
                                    //TODO handle errors
                                } else {
                                    setErrors({});
                                    setBody((prev: any) => {
                                        const newBody = prev.map((item: any) => {
                                            if (item.id === "-1") {
                                                return {
                                                    ...item,
                                                    id: index.toString()
                                                };
                                            }
                                            return item;
                                        });
                                        setSavedEnv(newBody);
                                        return newBody;
                                    });
                                    if (mode === "EDIT") {
                                        let bodyToSubmit = body?.find(x => x?.id === editing);
                                        if (bodyToSubmit) {
                                            const allowedKeys = [
                                                "entratelCode",
                                                "siteCode",
                                                "description",
                                                "files",
                                                "password"
                                            ];
                                            Object?.keys(bodyToSubmit)?.forEach(
                                                key => allowedKeys.includes(key) || delete bodyToSubmit[key]
                                            );
                                            onSubmit?.({
                                                securityEnvironments: [bodyToSubmit]
                                            });
                                        }
                                    }
                                    setEditing("false");
                                }
                            }}
                        >
                            <SecurityEnvironmentsData
                                key={`security-env-data-${box.id}-${index}`}
                                body={box}
                                onChange={(data: any, key: any, value: any) => {
                                    setDisplayErrors((prev: any) => ({
                                        ...prev,
                                        [key]: false
                                    }));

                                    handleChange(box.id, data);

                                    if (data?.expirationDate) {
                                        setErrors((prev: any) => {
                                            return {
                                                ...prev,
                                                expirationDate: false,
                                                password: false
                                            };
                                        });
                                    }
                                }}
                                editing={editing === box.id}
                                onError={(err: any) => {
                                    setErrors((prev: any) => {
                                        return {
                                            ...prev,
                                            ...err
                                        };
                                    });
                                }}
                                editableFields={
                                    mode === "EDIT" && editing !== "-1"
                                        ? {
                                              siteCode: false,
                                              description: false
                                          }
                                        : null
                                }
                                displayErrors={displayErrors}
                                required
                            />
                        </BoxForm>
                    ))}
                {!loadingSecurityEnv && !loadingPost && (
                    <Button
                        variant="text"
                        sx={{ width: "fit-content" }}
                        onClick={handleAddSecurityEnv}
                        disabled={editing === "-1"}
                    >
                        <Stack spacing={1} direction={"row"}>
                            <Box>
                                <FontAwesomeIcon icon={faPlusCircle} />
                            </Box>
                            <Box>Aggiungi ambiente di sicurezza</Box>
                        </Stack>
                    </Button>
                )}
            </Box>

            <Box
                component={"div"}
                gap={1}
                flexDirection={"column"}
                display={tabValue === "signaturecert" ? "flex" : "none"}
            >
                <FormHeaderTitle
                    title={
                        <Stack direction={"row"} spacing={2} alignItems={"center"}>
                            <Box marginTop={"-5px"}>
                                <ExtendedTypography variant="titleSmall" color="primary.textSubduedColor">
                                    Certificato di firma
                                </ExtendedTypography>
                            </Box>

                            {data?.type !== "P" && (
                                <Box pl={2}>
                                    <FormControlLabel
                                        control={
                                            <Toggle
                                                onChange={(e: BaseSyntheticEvent) => {
                                                    setHandleCertificate(e?.target?.checked);
                                                    if (mode === "EDIT" && !e?.target?.checked) {
                                                        if (data?.defaultSigner?.taxId) {
                                                            setEditSigner(true);
                                                        }
                                                        setSigner(null);
                                                    } else if (mode === "EDIT" && e?.target?.checked) {
                                                        if (data?.defaultSigner?.taxId) {
                                                            setEditSigner(false);
                                                            setSigner(data?.defaultSigner?.taxId);
                                                            setEditingCompanyCertificate(false);
                                                        } else {
                                                            setEditSigner(false);
                                                            setEditingCompanyCertificate(true);
                                                            setSigner(null);
                                                        }
                                                    }
                                                }}
                                                checked={handleCertificate}
                                            />
                                        }
                                        label="gestisci per l’intermediario"
                                        sx={{ display: "flex", alignItems: "center" }}
                                    />
                                </Box>
                            )}
                        </Stack>
                    }
                    sx={{ marginBottom: 0 }}
                />

                <InfoBox
                    description={
                        <Stack>
                            <Box>
                                <Trans
                                    i18nKey={`intermediaries.signatureTools.${
                                        dataSigners?.length === 0 && data?.type === "P"
                                            ? "infoNoSignersCertificate"
                                            : "signatureCertificateInfo"
                                    }`}
                                    components={{ br: <br />, b: <strong /> }}
                                />
                            </Box>
                            <Box></Box>
                        </Stack>
                    }
                    sx={{ marginBottom: 2 }}
                />

                {dataSigners?.length !== 0 && data?.type === "P" && (
                    <BoxForm disableHeader body={data} editing={false}>
                        <Grid container>
                            <Grid xs={4}>
                                <Name
                                    label="Nome e cognome"
                                    value={`${data?.details?.name} ${data?.details?.surname}`}
                                    editing={false}
                                />
                            </Grid>
                            <Grid xs={4}>
                                <Description label="Codice fiscale" value={data?.taxId} editing={false} />
                            </Grid>
                            <Grid xs={4} display={"flex"} justifyContent={"flex-end"}>
                                {!signer ? (
                                    <Button
                                        variant="text"
                                        disabled={linkingSigner}
                                        onClick={() => {
                                            setLinkingSigner(true);
                                            if (mode === "EDIT") {
                                                setEditSigner(true);
                                            }
                                            setTimeout(() => {
                                                setSigner(data?.taxId);
                                                setLinkingSigner(false);
                                            }, 200);
                                        }}
                                    >
                                        <FontAwesomeIcon
                                            style={{ marginRight: 8 }}
                                            spin={linkingSigner}
                                            icon={linkingSigner ? faSpinnerThird : faCheck}
                                        />{" "}
                                        Conferma certificato
                                    </Button>
                                ) : (
                                    <Button
                                        variant="text"
                                        disabled={linkingSigner}
                                        onClick={() => {
                                            setLinkingSigner(true);
                                            if (mode === "EDIT") {
                                                setEditSigner(true);
                                            }
                                            setTimeout(() => {
                                                setSigner(null);
                                                setLinkingSigner(false);
                                            }, 200);
                                        }}
                                    >
                                        <FontAwesomeIcon
                                            style={{ marginRight: 8 }}
                                            spin={linkingSigner}
                                            icon={linkingSigner ? faSpinnerThird : faLinkSlash}
                                        />{" "}
                                        Scollega certificato
                                    </Button>
                                )}
                            </Grid>
                        </Grid>
                    </BoxForm>
                )}

                {!editingCompanyCertificate && handleCertificate && data?.type === "C" && (
                    <BoxForm disableHeader body={data} editing={false}>
                        <Grid container>
                            <Grid xs={4}>
                                <Name
                                    label="Nome e cognome"
                                    value={`${data?.defaultSigner?.firstName} ${data?.defaultSigner?.lastName}`}
                                    editing={false}
                                />
                            </Grid>
                            <Grid xs={4}>
                                <Description
                                    label="Codice fiscale"
                                    value={data?.defaultSigner?.taxId}
                                    editing={false}
                                />
                            </Grid>
                            <Grid xs={4} display={"flex"} justifyContent={"flex-end"}>
                                <Button
                                    variant="text"
                                    disabled={linkingSigner}
                                    onClick={() => {
                                        setEditingCompanyCertificate(true);
                                        setEditSigner(true);
                                        setSigner(null);
                                        setLoadSignerSelected(true);
                                    }}
                                >
                                    <FontAwesomeIcon style={{ marginRight: 8 }} icon={faPenToSquare} /> Modifica
                                </Button>
                            </Grid>
                        </Grid>
                    </BoxForm>
                )}

                {editingCompanyCertificate && handleCertificate && data?.type === "C" && (
                    <>
                        <SignersLookup
                            onSelect={val => {
                                setSigner(val?.fiscalCode);
                                if (mode === "EDIT") {
                                    setEditSigner(true);
                                }
                            }}
                            onClear={() => {
                                setSigner(null);
                                if (mode === "EDIT") {
                                    setEditSigner(true);
                                }
                            }}
                        />

                        <FormHelperText
                            error={displayErrors?.signer}
                            sx={{ display: displayErrors?.signer ? "block" : "none" }}
                        >
                            Ricerca e seleziona un firmatario
                        </FormHelperText>
                    </>
                )}
            </Box>

            {mode === "CREATE" && (
                <IntermediaryFooterForm
                    onBack={() => {
                        setDisplayErrors(false);
                        onBack();
                    }}
                    onForward={handleSubmit}
                    disabledForward={loadingPost || !savedEnv}
                    loading={loadingPost}
                    isNew={true}
                    lastStep={false}
                />
            )}

            {mode === "EDIT" && tabValue === "signaturecert" && editSigner && (
                <IntermediaryFooterForm
                    onBack={
                        data?.defaultSigner?.taxId
                            ? () => {
                                  setSigner(data?.defaultSigner?.taxId);
                                  setEditingCompanyCertificate(false);
                                  setEditSigner(false);
                                  setHandleCertificate(data?.defaultSigner?.taxId ? true : false);
                                  setDisplayErrors(prev => {
                                      return { ...prev, signer: false };
                                  });
                              }
                            : undefined
                    }
                    onForward={handleSaveSigner}
                    disabledForward={loadingPatch}
                    loading={loadingPatch}
                    isNew={false}
                    lastStep={false}
                />
            )}
        </Stack>
    );
};
